/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError, Subject, from, EMPTY } from 'rxjs'
import { catchError, switchMap, filter, take } from 'rxjs/operators'
import { UnespCoreAuthService } from '../../services'
import { AuthService } from 'src/app/services/auth.service'
import { environment } from 'src/environments/environment'

@Injectable({
  providedIn: 'root',
})
export class UnespCoreAuthInterceptorService implements HttpInterceptor {
  private headerXsrf = 'X-XSRF-TOKEN'
  private isRefreshing = false
  private refreshResult$!: Subject<string | null>

  constructor(private unespCoreAuthService: UnespCoreAuthService, private authService: AuthService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const tokenStr = this.authService.getAccessToken

    request = request.clone({
      withCredentials: true,
      ...(tokenStr && !request.headers.has(this.headerXsrf)
        ? { headers: request.headers.set('authorization', 'Bearer ' + tokenStr) }
        : {}),
    })

    return next.handle(request).pipe(
      catchError(err => {
        if (!(err instanceof HttpErrorResponse)) return throwError(() => err)

        if (err.status === 401 && !request.url.includes('auth/logout') && request.url.startsWith(environment.baseUrlApi)) {
          return this.handle401(request, next)
        }

        if (err.status === 403 && request.url.includes('auth/refresh-token')) {
          this.unespCoreAuthService.logout('Sessão expirada', true)
        }

        return throwError(() => err)
      })
    )
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshResult$.pipe(
        take(1),
        switchMap(token => {
          if (token) {
            return next.handle(this.addToken(request, token))
          }
          return EMPTY
        })
      )
    }

    this.isRefreshing = true
    this.refreshResult$ = new Subject<string | null>()

    return from(this.authService.refreshTokenAndGetNew()).pipe(
      switchMap(newToken => {
        this.isRefreshing = false
        this.refreshResult$.next(newToken)
        this.refreshResult$.complete()
        return next.handle(this.addToken(request, newToken))
      }),
      catchError(err => {
        this.isRefreshing = false
        this.refreshResult$.next(null)
        this.refreshResult$.complete()
        this.unespCoreAuthService.logout('Sessão expirada', true)
        return EMPTY
      })
    )
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('authorization', 'Bearer ' + token),
      withCredentials: true,
    })
  }
}
